import React, { useState } from "react";
import { Trash2, AlertTriangle } from "lucide-react";
import Modal from "./Modal";
import { deleteAdmin } from "../../utils/deleteAdmin";
import { toast } from "react-toastify";
import { RawAdmin } from "../../utils/getAdmins";

interface ConfirmDeleteAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  admin: RawAdmin | null;
  onAdminDeleted: () => void;
}

const ConfirmDeleteAdminModal: React.FC<ConfirmDeleteAdminModalProps> = ({
  isOpen,
  onClose,
  admin,
  onAdminDeleted,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!admin) return;

    setIsDeleting(true);
    try {
      const { error } = await deleteAdmin(admin.id);
      if (error) {
        toast.error("Failed to delete admin: " + error);
        return;
      }

      toast.success("Admin deleted successfully");
      onAdminDeleted();
      onClose();
    } catch (error) {
      console.error("Error deleting admin:", error);
      toast.error("Failed to delete admin");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!admin) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Admin">
      <div className="space-y-4">
        {/* Warning Icon */}
        <div className="flex items-center justify-center">
          <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
        </div>

        {/* Warning Message */}
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Are you sure you want to delete this admin?
          </h3>
          <p className="text-sm text-gray-600">
            This action cannot be undone. The admin will be permanently removed
            from the system.
          </p>
        </div>

        {/* Admin Info */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm">
            <div className="font-medium text-gray-900">{admin.fullname}</div>
            <div className="text-gray-600">Email: {admin.email}</div>
            <div className="text-gray-600">District: {admin.districtName}</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Admin
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmDeleteAdminModal;
